<script lang="ts">
  import RedirectTable from './components/RedirectTable.svelte';
  import {
    RedirectService,
    Redirect,
    Column,
    SortDirection,
    CreateRedirect
  } from './utils/redirect.service.js';
  import Toasts from './components/toast/Toasts.svelte';
  import { addToast } from './components/toast/store.js';
  import CreateEditModal from './components/modals/redirect/CreateEditModal.svelte';
  import DeleteModal from './components/modals/DeleteModal.svelte';
  import ImportModal from './components/modals/ImportModal.svelte';
  import ActionBar from './components/ActionBar.svelte';
  import ChangeLogModal from './components/modals/ChangeLogModal.svelte';
  import ExportModal from './components/modals/ExportModal.svelte';
  import { isLoggedIn } from './utils/login.service.js';
  import { navigate } from 'svelte-routing';
  import { EtService } from './utils/et.service.js';
  import { SelectOption } from './components/form/SelectWithLabel.svelte';
  import { TagService } from './utils/tag.service.js';
  import { getUserErrorMessage } from './utils/error.js';
  import Main from './components/Main.svelte';

  if (!$isLoggedIn) {
    navigate('/login');
  }

  const redirectService = new RedirectService();
  const etService = new EtService();
  const tagService = new TagService();
  const pageSize = 50;
  const columns: Column[] = [
    {
      label: 'Quelle',
      sortable: true,
      field: 'source',
      formatter: rawFieldContent =>
        `<a href="https://www.adac.de${rawFieldContent}" target="_blank">${rawFieldContent}</a>`
    },
    {
      label: 'Ziel',
      sortable: true,
      field: 'destination',
      formatter: rawFieldContent =>
        `<a href="${rawFieldContent}" target="_blank">${rawFieldContent}</a>`
    },
    {
      label: 'Typ',
      sortable: false,
      field: 'redirect_type'
    },
    {
      label: 'Tag',
      sortable: false,
      field: 'tag'
    },
    {
      label: 'Statuscode',
      sortable: false,
      field: 'status_code'
    },
    {
      label: 'Gültig von',
      sortable: true,
      field: 'start_date',
      formatter: rawFieldContent =>
        rawFieldContent ? new Date(rawFieldContent).toLocaleString('de-DE') : ''
    },
    {
      label: 'Gültig bis',
      sortable: true,
      field: 'expiration_date',
      formatter: rawFieldContent =>
        rawFieldContent ? new Date(rawFieldContent).toLocaleString('de-DE') : ''
    },
    {
      label: 'Letzte Änderung',
      sortable: true,
      field: 'updated_at',
      formatter: rawFieldContent => new Date(rawFieldContent).toLocaleString('de-DE')
    }
  ];
  let tagOptions: SelectOption[] = [];

  let redirects: Redirect[] = [];
  let totalCount: number = 0;
  let currentPageIndex: number = 0;
  let sortBy: string | undefined = 'source';
  let sortDirection: SortDirection = 'DESC';

  let deleteModalOpen: boolean;
  let createOrEditModalOpen: boolean;
  let importModalOpen: boolean;
  let changeLogModalOpen: boolean;
  let exportModalOpen: boolean;

  let redirectToDelete: Redirect;
  let redirectToEdit: Redirect | undefined;
  let redirectForLogs: Redirect | undefined;
  let search: string = '';
  let selectedTag: string | undefined;

  const toggleImportModalOpen = () => {
    importModalOpen = !importModalOpen;
  };

  const toggleExportModalOpen = () => {
    exportModalOpen = !exportModalOpen;
  };

  const toggleCreateOrEditModalOpen = () => {
    createOrEditModalOpen = !createOrEditModalOpen;
  };

  const updateRedirectList = async () => {
    const { data, totalCount: responseTotalCount } = await redirectService.getList({
      limit: pageSize,
      sortBy,
      sortDirection,
      offset: currentPageIndex * pageSize,
      search,
      tag: selectedTag === 'all' ? '' : selectedTag
    });
    redirects = data;
    totalCount = responseTotalCount;

    await getTagOptions();
  };

  const createRedirect = async (redirect: CreateRedirect) => {
    if (!redirect.source || !redirect.destination) {
      addToast({
        type: 'error',
        message: 'Bitte alle Felder ausfüllen.'
      });
      return;
    }

    try {
      await redirectService.createRedirect(redirect);
      await updateRedirectList();
      addToast({
        type: 'success',
        message: 'Redirect wurde erfolgreich hinzugefügt.'
      });
      toggleCreateOrEditModalOpen();
    } catch (e) {
      addToast({
        type: 'error',
        message: getUserErrorMessage(e, 'Unerwarteter Fehler beim Hinzufügen des Redirects.')
      });
    }
  };

  const editRedirect = async (redirect: Redirect) => {
    if (!redirect.source || !redirect.destination) {
      addToast({
        type: 'error',
        message: 'Bitte alle Felder ausfüllen.'
      });
      return;
    }

    try {
      await redirectService.updateRedirect(redirect);
      await updateRedirectList();
      addToast({
        type: 'success',
        message: 'Redirect wurde erfolgreich geändert.'
      });
      toggleCreateOrEditModalOpen();
    } catch (e) {
      addToast({
        type: 'error',
        message: getUserErrorMessage(e, 'Unerwarteter Fehler beim Ändern des Redirects.')
      });
    }
  };

  const deleteRedirect = async (redirect: Redirect) => {
    try {
      await redirectService.delete(redirect.id);
      await updateRedirectList();
      addToast({
        type: 'success',
        message: 'Redirect wurde erfolgreich gelöscht.'
      });
    } catch (e) {
      addToast({
        type: 'error',
        message: getUserErrorMessage(e, 'Unerwarteter Fehler beim Löschen des Redirects.')
      });
    }
  };

  const validateRedirect = async (redirect: Redirect) => {
    try {
      await redirectService.validate(redirect.id);
      await updateRedirectList();
      addToast({
        type: 'success',
        message: 'Redirect wurde erfolgreich validiert.'
      });
    } catch (e) {
      addToast({
        type: 'error',
        message: getUserErrorMessage(e, 'Unbekannter Fehler: Redirect-Validierung fehlgeschlagen.')
      });
    }
  };

  const publishRedirects = async () => {
    try {
      await etService.publish('redirects');
      addToast({
        type: 'success',
        message: 'Das Veröffentlichen der Redirects wurde erfolgreich angestoßen.'
      });
    } catch (e) {
      addToast({
        type: 'error',
        message: 'Das Veröffentlichen der Redirects konnte nicht angestoßen werden.'
      });
    }
  };

  const onSelectedTagChange = async (event: Event) => {
    selectedTag = (event.target as HTMLSelectElement)?.value ?? '';

    await updateRedirectList();
  };

  const getTagOptions = async () => {
    try {
      const tags = await tagService.getTags();

      const tagSelectOptions = tags.map((tag: string) => ({
        label: tag,
        value: tag
      }));

      tagSelectOptions.unshift({
        label: 'Alle Tags',
        value: 'all'
      });

      tagOptions = tagSelectOptions;
    } catch (error) {
      addToast({
        type: 'error',
        message: 'Tags konnten nicht abgerufen werden.'
      });
    }
  };

  getTagOptions();

  $: sortBy, sortDirection, currentPageIndex, updateRedirectList(), getTagOptions();
</script>

<Main>
  <ActionBar
    bind:search
    updateEntryList={updateRedirectList}
    publishEntries={publishRedirects}
    {toggleImportModalOpen}
    {toggleExportModalOpen}
    {toggleCreateOrEditModalOpen}
    selectLabel="Tags: "
    selectPlaceholder="Wählen Sie eine Option"
    selectOptions={tagOptions}
    selectedOption={selectedTag}
    onSelectOptionChange={onSelectedTagChange}
  />

  <RedirectTable
    {redirects}
    {columns}
    {totalCount}
    {pageSize}
    bind:currentPageIndex
    bind:sortBy
    bind:sortDirection
    onDeleteClick={redirect => {
      redirectToDelete = redirect;
      deleteModalOpen = true;
    }}
    onEditClick={redirect => {
      redirectToEdit = redirect;
      createOrEditModalOpen = true;
    }}
    onValidateClick={redirect => {
      validateRedirect(redirect);
    }}
    onInspectLogsClick={redirect => {
      redirectForLogs = redirect;
      changeLogModalOpen = true;
    }}
  />

  <section data-role="modals">
    <CreateEditModal
      bind:isOpen={createOrEditModalOpen}
      redirect={redirectToEdit}
      onCreateRedirect={createRedirect}
      onUpdateRedirect={editRedirect}
    />

    <DeleteModal bind:isOpen={deleteModalOpen} onDelete={() => deleteRedirect(redirectToDelete)} />

    <ImportModal
      bind:isOpen={importModalOpen}
      onImportSuccess={async importedRedirectsCount => {
        toggleImportModalOpen();
        await updateRedirectList();
        addToast({
          type: 'success',
          message: `${importedRedirectsCount} Weiterleitungen wurden erfolgreich importiert.`
        });
      }}
    />

    <ChangeLogModal
      bind:isOpen={changeLogModalOpen}
      redirect={redirectForLogs}
      getRedirectLogs={redirectService.getLogs}
    />

    <ExportModal bind:isOpen={exportModalOpen} getAllRedirects={redirectService.getList} />
  </section>

  <Toasts />
</Main>

<style lang="scss">
  @use './styles/spacing';

  :global(body) {
    min-height: 100vh;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    font-family: ADACSans, Arial, sans-serif;
  }
</style>
